import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { api, Dhis2Target, Facility, Permission } from '../lib/api';
import { relativeTime } from '../lib/format';
import { useClientPagination } from '../lib/pagination';
import { Card, TablePagination, useToast } from '../components/ui';

export function Facilities({ can }: { can: (permission: Permission) => boolean }) {
  const [rows, setRows] = useState<Facility[]>([]);
  const [targets, setTargets] = useState<Dhis2Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Facility | 'new' | null>(null);
  const [issuedKey, setIssuedKey] = useState<{ facility: string; key: string } | null>(null);
  const pagination = useClientPagination(rows);
  const toast = useToast();

  const canEdit = can('facilities.write');
  const canRotate = can('facilities.rotate_key');
  const canDelete = can('facilities.delete');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [facilities, targetList] = await Promise.all([
        api.get<Facility[]>('/api/facilities'),
        api.get<Dhis2Target[]>('/api/targets'),
      ]);
      setRows(facilities);
      setTargets(targetList);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function rotateKey(facility: Facility) {
    if (!confirm(`Issue a new API key for ${facility.name}? The current key stops working immediately.`)) {
      return;
    }
    try {
      const result = await api.post<{ apiKey: string }>(`/api/facilities/${facility.id}/rotate-key`);
      setIssuedKey({ facility: facility.name, key: result.apiKey });
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }

  async function removeFacility(facility: Facility) {
    if (!confirm(`Delete ${facility.name}? Its submission history is deleted too.`)) {
      return;
    }
    try {
      await api.del(`/api/facilities/${facility.id}`);
      toast('ok', `${facility.name} removed`);
      await load();
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <div className="facilities-toolbar">
        <span className="facilities-toolbar-helper">
          Each facility authenticates with its own API key, so one facility's credentials never give
          access to another's data.
        </span>
        {canEdit ? (
          <Button
            kind="primary"
            size="md"
            renderIcon={Add}
            onClick={() => setEditing('new')}
          >
            Add facility
          </Button>
        ) : null}
      </div>

      <Card bodyless>
        <div className="facilities-results-panel">
          {loading && rows.length === 0 ? (
            <div className="facilities-results-empty">
              <InlineLoading description="Loading facilities…" />
            </div>
          ) : rows.length === 0 ? (
            <div className="facilities-results-empty">
              <div className="facilities-empty-title">No facilities yet</div>
              <div className="facilities-empty-desc">
                Add a facility to issue it an API key and assign its DHIS2 organisation unit.
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Table size="sm">
                <TableHead>
                  <TableRow>
                    <TableHeader>Facility</TableHeader>
                    <TableHeader>Code</TableHeader>
                    <TableHeader>DHIS2 org unit</TableHeader>
                    <TableHeader>DHIS2 server</TableHeader>
                    <TableHeader>API key</TableHeader>
                    <TableHeader>Last seen</TableHeader>
                    <TableHeader />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagination.pageItems.map((facility) => (
                    <TableRow key={facility.id}>
                      <TableCell>
                        <div className="stack">
                          <span style={{ fontWeight: 560 }}>{facility.name}</span>
                          {!facility.enabled ? (
                            <Tag type="gray" size="sm" className="facility-status-tag">
                              Disabled
                            </Tag>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="mono">{facility.code}</TableCell>
                      <TableCell>
                        {facility.dhis2OrgUnit ? (
                          <span className="mono">{facility.dhis2OrgUnit}</span>
                        ) : (
                          <Tag type="red" size="sm" className="facility-status-tag">
                            Not set
                          </Tag>
                        )}
                      </TableCell>
                      <TableCell className="muted">{facility.targetName ?? 'Default server'}</TableCell>
                      <TableCell className="mono faint">{facility.apiKeyHint ?? '—'}</TableCell>
                      <TableCell className="muted">{relativeTime(facility.lastSeenAt)}</TableCell>
                      <TableCell>
                        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          {canEdit ? (
                            <Button
                              kind="ghost"
                              size="sm"
                              onClick={() => setEditing(facility)}
                            >
                              Edit
                            </Button>
                          ) : null}
                          {canRotate ? (
                            <Button
                              kind="tertiary"
                              size="sm"
                              onClick={() => rotateKey(facility)}
                            >
                              New key
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              onClick={() => removeFacility(facility)}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rows.length > 0 ? <TablePagination {...pagination} /> : null}
        </div>
      </Card>

      {editing ? (
        <FacilityModal
          facility={editing === 'new' ? null : editing}
          targets={targets}
          onClose={() => setEditing(null)}
          onSaved={async (key, name) => {
            setEditing(null);
            if (key) setIssuedKey({ facility: name, key });
            await load();
          }}
        />
      ) : null}

      {issuedKey ? <ApiKeyModal issued={issuedKey} onClose={() => setIssuedKey(null)} /> : null}
    </>
  );
}

function FacilityModal({
  facility,
  targets,
  onClose,
  onSaved,
}: {
  facility: Facility | null;
  targets: Dhis2Target[];
  onClose: () => void;
  onSaved: (apiKey: string | null, name: string) => void;
}) {
  const [code, setCode] = useState(facility?.code ?? '');
  const [name, setName] = useState(facility?.name ?? '');
  const [orgUnit, setOrgUnit] = useState(facility?.dhis2OrgUnit ?? '');
  const [targetId, setTargetId] = useState(facility?.targetId ? String(facility.targetId) : '');
  const [enabled, setEnabled] = useState(facility?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  // DHIS2 identifiers are 11 characters starting with a letter; catching that here saves a round
  // trip and a failed submission later.
  const orgUnitValid = orgUnit === '' || /^[A-Za-z][A-Za-z0-9]{10}$/.test(orgUnit);

  async function save() {
    setBusy(true);
    try {
      const payload = {
        code: code.trim(),
        name: name.trim(),
        dhis2OrgUnit: orgUnit.trim() || null,
        targetId: targetId ? Number(targetId) : null,
        enabled,
      };
      if (facility) {
        await api.patch(`/api/facilities/${facility.id}`, payload);
        toast('ok', `${payload.name} saved`);
        onSaved(null, payload.name);
      } else {
        const created = await api.post<{ facility: Facility; apiKey: string }>('/api/facilities', payload);
        onSaved(created.apiKey, payload.name);
      }
    } catch (error) {
      toast('error', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComposedModal open onClose={onClose} size="sm" className="facilities-modal">
      <ModalHeader title={facility ? `Edit ${facility.name}` : 'Add facility'} closeModal={onClose} />
      <ModalBody>
        <div className="facilities-modal-form">
          <TextInput
            id="facility-name-input"
            labelText="Facility name"
            placeholder="Juba Teaching Hospital"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <TextInput
            id="facility-code-input"
            labelText="Facility code"
            helperText="What the facility's OpenMRS puts in its reports. Cannot be changed after creation."
            placeholder="JUBA-TH"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={Boolean(facility)}
            className="facility-input-mono"
          />

          <TextInput
            id="facility-orgunit-input"
            labelText="DHIS2 organisation unit"
            helperText={
              orgUnitValid
                ? 'The UID this facility reports as. Submissions fail until it is set.'
                : undefined
            }
            invalid={!orgUnitValid}
            invalidText="A DHIS2 UID is 11 characters and starts with a letter."
            placeholder="XBWKFzPiBRi"
            value={orgUnit}
            onChange={(event) => setOrgUnit(event.target.value)}
            className="facility-input-mono"
          />

          <Select
            id="facility-target-select"
            labelText="DHIS2 server"
            helperText="Leave on the default unless this facility reports elsewhere."
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
          >
            <SelectItem value="" text="Use the default server" />
            {targets.map((target) => (
              <SelectItem
                key={target.id}
                value={String(target.id)}
                text={`${target.name}${target.dryRun ? ' (dry run)' : ''}`}
              />
            ))}
          </Select>

          <Checkbox
            id="facility-enabled-checkbox"
            labelText="Accept submissions from this facility"
            checked={enabled}
            onChange={(_event, { checked }) => setEnabled(checked)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={save}
          disabled={busy || !code.trim() || !name.trim() || !orgUnitValid}
        >
          {busy ? <InlineLoading status="active" description="" /> : null}
          {facility ? 'Save changes' : 'Create and issue key'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}

function ApiKeyModal({
  issued,
  onClose,
}: {
  issued: { facility: string; key: string };
  onClose: () => void;
}) {
  const toast = useToast();
  return (
    <ComposedModal open onClose={onClose} size="sm" className="facilities-modal api-key-modal">
      <ModalHeader title={`API key for ${issued.facility}`} closeModal={onClose} />
      <ModalBody>
        <div className="api-key-modal-body">
          <InlineNotification
            kind="warning"
            title=""
            subtitle="This is the only time the key is shown. It is stored hashed and cannot be recovered — if it is lost, issue a new one."
            hideCloseButton
            className="api-key-warning-notification"
          />

          <div className="key-reveal">{issued.key}</div>

          <div className="stack small muted">
            <span>In that facility's OpenMRS, set:</span>
            <span className="mono">ssemrws.dhis2.fhirAuthHeader = Bearer {issued.key}</span>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          onClick={() => {
            navigator.clipboard.writeText(issued.key).then(
              () => toast('ok', 'Key copied'),
              () => toast('error', 'Could not copy; select it manually'),
            );
          }}
        >
          Copy key
        </Button>
        <Button kind="primary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
